import { createSelector } from 'reselect'
import { AdsInitialStateType } from '../reducers/ads'

type AdsType = { ads: AdsInitialStateType }

// PLOP_APPEND_PATTERN_ANCHOR

export const adsSelector = createSelector(
  ({ ads }: AdsType) => ads.ads.results,
  results => results.filter(item => item.id)
)

export const adsTotalsSelector = createSelector(
  ({ ads }: AdsType) => ads.ads.results,
  results => results.filter(item => !item.name && !item.id)[0]
)

export const adsIsLoadingSelector = createSelector(
  ({ ads }: AdsType) => ads.ads.isLoading,
  isLoading => isLoading
)

export const adsErrorSelector = createSelector(
  ({ ads }: AdsType) => ads.ads.error,
  error => error
)

export const adsWasLoadedSelector = createSelector(
  ({ ads }: AdsType) => ads.ads.wasLoaded,
  wasLoaded => wasLoaded
)

export const adsAfterSelector = createSelector(
  ({ ads }: AdsType) => ads.ads.paging.cursors?.after,
  after => after
)

export const adsNextSelector = createSelector(
  ({ ads }: AdsType) => ads.ads.paging.next,
  next => next
)

// CREATE
export const adCreatedDataSelector = createSelector(
  ({ ads }: AdsType) => ads.createdAd.data,
  data => data
)

export const adCreateLoadingSelector = createSelector(
  ({ ads }: AdsType) => ads.createdAd.isLoading,
  isLoading => isLoading
)

export const adWasCreatedSelector = createSelector(
  ({ ads }: AdsType) => ads.createdAd.wasCreated,
  wasCreated => wasCreated
)

export const adCreateErrorSelector = createSelector(
  ({ ads }: AdsType) => ads.createdAd.error,
  error => error
)

// VALIDATE CREATE AD
export const validatedDomainsSelector = createSelector(
  ({ ads }: AdsType) => ads.validatedAd.domains,
  domains => domains
)

// Create ads batch
export const createAdsBatchSelector = createSelector(
  ({ ads }: AdsType) => ads.createAdsBatch.data,
  data => data
)

export const createAdsBatchIsLoadingSelector = createSelector(
  ({ ads }: AdsType) => ads.createAdsBatch.isLoading,
  isLoading => isLoading
)

export const createAdsBatchErrorSelector = createSelector(
  ({ ads }: AdsType) => ads.createAdsBatch.error,
  error => error
)

export const createAdsBatchWasCreatedSelector = createSelector(
  ({ ads }: AdsType) => ads.createAdsBatch.wasCreated,
  wasCreated => wasCreated
)

// UPDATE
export const adUpdateSelector = createSelector(
  ({ ads }: AdsType) => ads.updatedAd,
  id => id
)

export const adUpdateIdSelector = createSelector(
  ({ ads }: AdsType) => ads.updatedAd.id,
  id => id
)

export const adUpdateErrorSelector = createSelector(
  ({ ads }: AdsType) => ads.updatedAd.error,
  error => error
)

export const adUpdateIsLoadingSelector = createSelector(
  ({ ads }: AdsType) => ads.updatedAd.isLoading,
  isLoading => isLoading
)

export const adWasUpdatedSelector = createSelector(
  ({ ads }: AdsType) => ads.updatedAd.wasUpdated,
  wasUpdated => wasUpdated
)

// DELETE
export const adDeleteItemSelector = createSelector(
  ({ ads }: AdsType) => ads.deletedAd,
  deletedAd => deletedAd
)

export const adDeleteItemIdSelector = createSelector(
  ({ ads }: AdsType) => ads.deletedAd.id,
  deletedAd => deletedAd
)

export const adDeleteIsLoadingSelector = createSelector(
  ({ ads }: AdsType) => ads.deletedAd.isLoading,
  isLoading => isLoading
)

export const adDeleteErrorSelector = createSelector(
  ({ ads }: AdsType) => ads.deletedAd.error,
  error => error
)

export const adDeleteWasDeletedSelector = createSelector(
  ({ ads }: AdsType) => ads.deletedAd.wasDeleted,
  wasDeleted => wasDeleted
)

// Approve internal ad
export const approveInternalAdIsLoadingSelector = (adId: string | number) =>
  createSelector(
    ({ ads }: AdsType) => ads.approveInternalAd[adId],
    data => data?.isLoading
  )

export const approveInternalAdErrorSelector = (adId: string) =>
  createSelector(
    ({ ads }: AdsType) => ads.approveInternalAd[adId],
    data => data?.error
  )

export const approveInternalAdWasApprovedSelector = (adId: string) =>
  createSelector(
    ({ ads }: AdsType) => ads.approveInternalAd[adId],
    data => data?.wasApproved
  )

// Disapprove internal ad
export const disapproveInternalAdIsLoadingSelector = (adId: string | number) =>
  createSelector(
    ({ ads }: AdsType) => ads.disapproveInternalAd[adId],
    data => data?.isLoading
  )

export const disapproveInternalAdErrorSelector = (adId: string) =>
  createSelector(
    ({ ads }: AdsType) => ads.disapproveInternalAd[adId],
    data => data?.error
  )

export const disapproveInternalAdWasDisapprovedSelector = (adId: string) =>
  createSelector(
    ({ ads }: AdsType) => ads.disapproveInternalAd[adId],
    data => data?.wasDisapproved
  )

// get single ad
export const adLoadingSelector = createSelector(
  ({ ads }: AdsType) => ads.ad.isLoading,
  isLoading => isLoading
)

export const adWasLoadedSelector = createSelector(
  ({ ads }: AdsType) => ads.ad.wasLoaded,
  wasLoaded => wasLoaded
)

export const adSelector = createSelector(
  ({ ads }: AdsType) => ads.ad.data,
  data => data
)

// ad copy
export const selectedAdForCopySelector = createSelector(
  ({ ads }: AdsType) => ads.selectedAdForCopy,
  selectedAdForCopy => selectedAdForCopy
)

export const copyAdIsLoadingSelector = createSelector(
  ({ ads }: AdsType) => ads.copyAd.isLoading,
  copyAd => copyAd
)
export const copyAdErrorSelector = createSelector(
  ({ ads }: AdsType) => ads.copyAd.error,
  error => error
)
export const copyAdWasCreatedSelector = createSelector(
  ({ ads }: AdsType) => ads.copyAd.wasCreated,
  wasCreated => wasCreated
)
export const copyAdDataSelector = createSelector(
  ({ ads }: AdsType) => ads.copyAd.data,
  data => data
)

// modified adData used for AdPreview to represent changed from EditForm
export const adModifiedDataSelector = createSelector(
  ({ ads }: AdsType) => ads.adPreview.adModifiedData,
  adModifiedData => adModifiedData
)
// adCarouselSelectedIndexes used for AdPreview and Carousel form to preselect active carousel item
export const adCarouselSelectedIndexesSelector = createSelector(
  ({ ads }: AdsType) => ads.adPreview.adCarouselSelectedIndexes,
  adCarouselSelectedIndexes => adCarouselSelectedIndexes
)

export const adPreviewIsSoundMutedSelector = createSelector(
  ({ ads }: AdsType) => ads.adPreview.isSoundMuted,
  isSoundMuted => isSoundMuted
)

export const adsSortSelector = createSelector(
  ({ ads }: AdsType) => ads.sort,
  sort => sort
)

// facebookPosts
export const facebookPostsSelector = createSelector(
  ({ ads }: AdsType) => ads.facebookPosts.results,
  results => results
)

export const facebookPostsIsLoadingSelector = createSelector(
  ({ ads }: AdsType) => ads.facebookPosts.isLoading,
  isLoading => isLoading
)

export const facebookPostsErrorSelector = createSelector(
  ({ ads }: AdsType) => ads.facebookPosts.error,
  error => error
)

export const facebookPostsWasLoadedSelector = createSelector(
  ({ ads }: AdsType) => ads.facebookPosts.wasLoaded,
  wasLoaded => wasLoaded
)
